import { WorkerAvailability } from '../../../../../models/workerAvailability'
import { AssistanceStatus } from '../../../../../models/assistanceStatus'
import { Assistance } from '../../../../../models/assistance'
import { AssistantAssignment } from '../../../../../models/assistantAssignment'
import { DateRange } from '../../../../../services/date-utils.service'
import { addMinutes, differenceInMinutes } from 'date-fns'

type IWorkerAvailabilitySelectItem = {
  availability: WorkerAvailability
  enabled?: boolean
  status?: AssistanceStatus
  flags?: WorkerAvailabilitySelectItemFlag[]
}

export enum WorkerAvailabilitySelectItemFlag {
  IsExchangeAssistance,
}

type NeighbourAssistances = {
  before?: Assistance // nearest earlier assistance of the assistant: max getDateTo() <= range.from
  after?: Assistance // nearest later assistance of the assistant: min getDateFrom() >= range.to
  previousTransitEnd?: Date // latest end-incl-transit among the earlier ones, only when it reaches past range.from
}

export class WorkerAvailabilitySelectItem implements IWorkerAvailabilitySelectItem {
  availability: WorkerAvailability
  enabled: boolean
  status: AssistanceStatus
  flags: WorkerAvailabilitySelectItemFlag[]
  private transitDurationMinutesValue: number
  private neighbours: NeighbourAssistances | undefined
  private range: DateRange | undefined
  private transitBoundsApply = false
  private appliedExistingAssignment: AssistantAssignment | undefined

  constructor(props: IWorkerAvailabilitySelectItem) {
    this.availability = props.availability
    this.enabled = props.enabled ?? true
    this.status = props.status
    this.flags = props.flags
  }

  get transitDurationMinutes(): number {
    return this.transitDurationMinutesValue
  }

  // Enforcing 0 <= transit <= gap-to-next-assistance
  set transitDurationMinutes(value: number) {
    this.transitDurationMinutesValue = Math.min(Math.max(value, 0), this.maxAllowedTransitMinutes ?? Infinity)
  }

  // The edited assistance's assignment of this item's assistant, as of the last applied neighbourhood. Items
  // are rebuilt and re-applied on every data emission, so this is as fresh as the rendered data.
  get existingAssignment(): AssistantAssignment | undefined {
    return this.appliedExistingAssignment
  }

  get hasExistingAssignment(): boolean {
    return !!this.appliedExistingAssignment
  }

  get assistanceBefore(): Assistance | undefined {
    return this.neighbours?.before
  }

  // Negative or zero — the template's danger colouring keys off the sign
  get assistanceBeforeHHMM(): string | undefined {
    if (!this.neighbours?.before || !this.range) return undefined
    return differenceInMinutes(this.neighbours.before.getDateTo(), this.range.from).toString(10)
  }

  get assistanceAfter(): Assistance | undefined {
    return this.neighbours?.after
  }

  get assistanceAfterHHMM(): string | undefined {
    if (!this.neighbours?.after || !this.range) return undefined
    return differenceInMinutes(this.neighbours.after.getDateFrom(), this.range.to).toString(10)
  }

  // Undefined means "no configured ceiling" (see WorkerSelectComponent#onTransitDurationChange) — only a real
  // number when there's an actual gap-based bound (the assistant's next assistance).
  get maxAllowedTransitMinutes(): number | undefined {
    if (!this.transitBoundsApply || !this.neighbours?.after || !this.range) return undefined
    return Math.max(0, differenceInMinutes(this.neighbours.after.getDateFrom(), this.range.to))
  }

  get nextAssistanceStart(): Date | undefined {
    return this.transitBoundsApply ? this.neighbours?.after?.getDateFrom() : undefined
  }

  get blockedByPreviousTransitUntil(): Date | undefined {
    return this.neighbours?.previousTransitEnd
  }

  /**
   * Applies the assistant's neighbour candidates (assistances fetched around the edited range) to this item.
   */
  applyAssistanceNeighbours(
    nearbyAssistanceCandidates: Assistance[],
    range: DateRange,
    existingAssignment: AssistantAssignment | undefined,
    defaultTransitMinutes: number,
  ): void {
    this.neighbours = this.findNeighbours(nearbyAssistanceCandidates, range)
    this.range = range
    this.transitBoundsApply = false
    this.appliedExistingAssignment = undefined
    if (!this.enabled) return

    if (this.neighbours.previousTransitEnd && !existingAssignment) {
      this.enabled = false
      return
    }

    this.transitBoundsApply = true
    this.appliedExistingAssignment = existingAssignment
    // The setter clamps to the gap-based ceiling, which is live now that transitBoundsApply is set
    this.transitDurationMinutes = existingAssignment
      ? (existingAssignment.transitDurationMinutes ?? 0)
      : defaultTransitMinutes
  }

  private findNeighbours(nearbyAssistances: Assistance[], range: DateRange): NeighbourAssistances {
    const assistantId = this.availability.worker.assistant.id
    const endWithTransit = (a: Assistance): Date =>
      addMinutes(
        a.getDateTo(),
        a.assistantAssignments.getAssignmentByAssistantId(assistantId)?.transitDurationMinutes ?? 0,
      )

    const neighbours: NeighbourAssistances = {}

    for (const a of nearbyAssistances) {
      if (!a.assistantAssignments.hasAssistant(assistantId)) continue

      if (a.getDateTo().getTime() <= range.from.getTime()) {
        if (!neighbours.before || neighbours.before.getDateTo() < a.getDateTo()) neighbours.before = a
        const blockingEnd = endWithTransit(a)
        if (
          blockingEnd.getTime() > range.from.getTime() &&
          (!neighbours.previousTransitEnd || blockingEnd > neighbours.previousTransitEnd)
        ) {
          neighbours.previousTransitEnd = blockingEnd
        }
      }
      if (a.getDateFrom().getTime() >= range.to.getTime()) {
        if (!neighbours.after || neighbours.after.getDateFrom() > a.getDateFrom()) neighbours.after = a
      }
    }

    return neighbours
  }
}
